/**
 * Q1b — N1 · Competência 2 · task-model H3.11.K02
 *
 *   "Dados dois vértices opostos de um retângulo ou quadrado no plano cartesiano,
 *    ESCOLHER, entre cinco alternativas, as coordenadas dos vértices restantes."
 *
 * O mesmo problema de H3.11.K01, no formato de múltipla escolha. Cada alternativa é um
 * par de pontos, e cada distrator é um erro típico nomeado — coordenadas invertidas, o
 * método do retângulo aplicado ao quadrado, a diagonal tratada como lado —, calculado
 * a partir dos números da variante em `_comum/quadrado.ts`.
 *
 * As alternativas não são números: vão EMBARALHADAS, e não em ordem crescente.
 */

import type { Vec2 } from '../../geom/vec.ts';
import { assembleSelectOne } from '../../engine/assemble.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  COORD_MISTURADAS,
  COORD_TROCADAS,
  QUAD_DIAGONAL_COMO_LADO,
  QUAD_GIRO_ERRADO,
  QUAD_LOSANGO,
  QUAD_RETANGULO,
  RET_FEZ_QUADRADO,
} from '../../schema/errors.ts';
import type { BuggySolver, ItemTemplate } from '../../schema/item.ts';
import { NOT_APPLICABLE, templateId } from '../../schema/item.ts';
import { strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';
import { mesmoConjunto, pontoTxt } from '../_comum/plano.ts';
import {
  CONTEXTOS_QUADRILATERO,
  descricaoDoPlano,
  errosDoQuadrilatero,
  figuraDoPlano,
  relacaoDosDados,
  resolucaoDoQuadrilatero,
} from '../_comum/quadrado.ts';
import { sortearParams, type Params } from '../h3-11-K01-quadrado/template.ts';

export type Par = readonly [Vec2, Vec2];

const textoDoPar = (v: Par): string => `B = ${pontoTxt(v[0])} e D = ${pontoTxt(v[1])}`;

/**
 * Um solucionador por erro típico. A faixa 1 leva, em cada nível, o erro que dá sentido
 * a ele: coordenadas invertidas e a diagonal como lado nos dois; no quadrado inclinado,
 * o método do retângulo; no retângulo, o quadrado inclinado no lugar dele.
 */
const porErro = (error: ErrorCode, priority: number): BuggySolver<Params, Par> => ({
  error,
  priority,
  solve: (p) => errosDoQuadrilatero(p.q).find((e) => e.error === error)?.pontos ?? NOT_APPLICABLE,
  explain: (p, v): RichText =>
    errosDoQuadrilatero(p.q).find((e) => e.error === error && mesmoConjunto(e.pontos, v))
      ?.explicacao ?? [txt(textoDoPar(v))],
});

const BUGGY: readonly BuggySolver<Params, Par>[] = [
  porErro(COORD_TROCADAS, 1),
  porErro(QUAD_DIAGONAL_COMO_LADO, 1),
  porErro(QUAD_RETANGULO, 1),
  porErro(RET_FEZ_QUADRADO, 1),
  porErro(COORD_MISTURADAS, 2),
  porErro(QUAD_LOSANGO, 2),
  porErro(QUAD_GIRO_ERRADO, 2),
];

export const quadradoEscolha: ItemTemplate<Params, Par> = {
  id: templateId('h3-11-K02-quadrado-escolha'),
  // v2: eixos numerados só na origem e nos extremos.
  version: 2,
  taskModel: taskModelCode('H3.11.K02'),
  skills: [skillCode('H3.11')],
  axis: 'II',
  level: 'N1',
  title: 'Quadrilátero no plano: escolher as coordenadas',
  responseModel: '3PL',

  difficultySources: ['steps.2'],

  params: sortearParams,
  solve: (p) => [p.q.B, p.q.D],
  buggy: BUGGY,
  // B e D trocados descrevem o mesmo quadrilátero: não são alternativas diferentes.
  equals: (x, y) => mesmoConjunto(x, y),
  distractorMinRelSep: 0,

  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.nivel === 'inclinado') fontes.push('steps.3plus', 'distractor-proximity.high');
    return fontes;
  },

  build(p, key, distractors, rng) {
    const ctx = CONTEXTOS_QUADRILATERO[p.contexto] as (typeof CONTEXTOS_QUADRILATERO)[number];
    const rel = relacaoDosDados(p.nivel);
    const interaction = assembleSelectOne<Par>({
      structure: 'E2',
      key,
      distractors,
      render: (v) => [txt(textoDoPar(v))],
      raw: textoDoPar,
      rng,
    });
    return {
      stem: [txt(`${ctx.abertura(p.nivel)} ${rel.antes}`), strong(rel.destaque), txt(rel.depois)],
      comando: [txt('Quais são as coordenadas dos vértices '), strong('B e D'), txt('?')],
      stimuli: [
        {
          kind: 'static-figure',
          motion: 'none',
          audio: { need: 'none' },
          id: 'plano',
          scene: figuraDoPlano(p.q),
          alt: descricaoDoPlano(p.q),
        },
      ],
      interaction,
      rationale: [strong('Resolução. '), ...resolucaoDoQuadrilatero(p.q)],
      declared: p.nivel === 'retangulo' ? 'facil' : 'media',
    };
  },
};
